import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useChannelDetail } from "../../hooks/useChannels";
import EditChannelModal from "../../components/modals/EditChannelModal";
import DeleteChannelModal from "../../components/modals/DeleteChannelModal";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Settings,
  MessageSquare,
  Activity,
  Smartphone,
  RefreshCw,
  Power,
  PowerOff,
  Edit3,
  Trash2,
  Wifi,
  CheckCircle,
  XCircle,
  Loader2,
  Bot,
  Calendar,
  Save,
  X,
  RotateCcw,
} from "lucide-react";
import {
  getStatusColor,
  getProviderColor,
  formatDate,
} from "../../data/mockChannelData";

const ChannelDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    channel: channelValue,
    isLoading,
    error,
    refetch,
    updateStatus,
    deleteChannel,
    updateChannelConfig,
    retryWhatsAppSetup,
    isRetrying,
  } = useChannelDetail(id);
  const channel = channelValue?.channel;

  console.log("Channel detail data:", channel);

  const handleSaveChannel = async (channelData) => {
    setIsSaving(true);
    try {
      const success = await updateChannelConfig(channelData);
      if (success) {
        // Determine what was changed for better user feedback
        let toastMessage = "Channel settings updated successfully!";

        // Check for agent assignment changes
        const oldAgentId = channel?.agent_id;
        const newAgentId = channelData.agent_id;

        if (oldAgentId !== newAgentId) {
          if (newAgentId) {
            toastMessage =
              "Channel settings and agent assignment updated successfully!";
          } else {
            toastMessage =
              "Channel settings updated and agent assignment disabled!";
          }
        }

        toast.success(toastMessage);
        setShowEditModal(false);
        refetch();
      }
    } catch (error) {
      console.error("Save channel error:", error);
      toast.error(error.message || "Failed to update channel settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteChannel = async () => {
    setIsDeleting(true);
    try {
      const success = await deleteChannel();
      if (success) {
        setShowDeleteModal(false);
        navigate("/admin/channels");
      }
    } catch (error) {
      console.error("Delete channel error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-lg text-gray-600 dark:text-gray-400">
              Loading channel details...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !channel) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-xl p-6 text-center">
          <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">
            Channel Not Found
          </h2>
          <p className="text-red-600 dark:text-red-400 mb-4">
            The channel you're looking for doesn't exist or has been deleted.
          </p>
          <button
            onClick={() => navigate("/admin/channels")}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
          >
            Back to Channels
          </button>
        </div>
      </div>
    );
  }

  const tabs = [{ id: "overview", label: "Overview", icon: Activity }];

  const renderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Info */}
      <div className="lg:col-span-2 space-y-6">
        {/* Status & Connection */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Connection Status
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-750 rounded-lg">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    channel.status === "active"
                      ? "bg-green-500"
                      : channel.status === "pending"
                      ? "bg-yellow-500"
                      : "bg-red-500"
                  }`}
                ></div>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Channel Status
                </span>
              </div>
              <span
                className={`px-3 py-1 rounded-lg text-sm font-medium ${getStatusColor(
                  channel.status
                )}`}
              >
                {channel.status}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-750 rounded-lg">
              <div className="flex items-center gap-3">
                <Wifi className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Connection
                </span>
              </div>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-sm font-medium">
                Connected
              </span>
            </div>
          </div>
        </div>

        {/* Conversation Stats - Basic Info Only */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Basic Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-750 rounded-lg">
              <div className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                {channel.type?.toUpperCase()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Channel Type
              </div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-750 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 mb-2">
                {channel.agent_name}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                AI Agent
              </div>
            </div>
          </div>
        </div>

        {/* Channel Configuration */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-green-600" />
            Channel Configuration
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-750 rounded-lg">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Business Name
                </label>
                <p className="text-gray-800 dark:text-white font-medium">
                  {channel.config?.business_name || "Not configured"}
                </p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-750 rounded-lg">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Channel Type
                </label>
                <p className="text-gray-800 dark:text-white font-medium capitalize">
                  {channel.type}
                </p>
              </div>
            </div>

            {channel.config?.features && (
              <div className="p-4 bg-gray-50 dark:bg-gray-750 rounded-lg">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-2">
                  Supported Features
                </label>
                <div className="flex flex-wrap gap-2">
                  {channel.config.features.map((feature, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Channel Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            Channel Information
          </h3>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Channel Name
              </label>
              <p className="text-gray-800 dark:text-white font-medium">
                {channel.name}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Agent Name
              </label>
              <p className="text-gray-800 dark:text-white">
                {channel.agent_name}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                AI Provider
              </label>
              <div
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium mt-1 ${getProviderColor(
                  channel.model_provider
                )}`}
              >
                <Bot className="w-3 h-3" />
                {channel.model_provider?.toUpperCase()}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Model
              </label>
              <p className="text-gray-800 dark:text-white">
                {channel.model_name || "Default model"}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Created
              </label>
              <p className="text-gray-800 dark:text-white">
                {formatDate(channel.created_at)}
              </p>
            </div>

            {channel.updated_at !== channel.created_at && (
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Last Updated
                </label>
                <p className="text-gray-800 dark:text-white">
                  {formatDate(channel.updated_at)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <p className="text-gray-600 dark:text-gray-400">
        Settings configuration coming soon...
      </p>
    </div>
  );

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin/channels")}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
              <Smartphone className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              {channel.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {channel.type} channel managed by {channel.agent_name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Retry WhatsApp button - show only for WhatsApp channels that are not active */}
          {channel.type === "whatsapp" && channel.status !== "active" && (
            <button
              onClick={() => retryWhatsAppSetup()}
              disabled={isRetrying}
              className="px-4 py-2 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-600 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRetrying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              {isRetrying ? "Retrying..." : "Retry Setup"}
            </button>
          )}
          <button
            onClick={() => setShowEditModal(true)}
            className="px-4 py-2 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors font-medium flex items-center gap-2"
          >
            <Edit3 className="w-4 h-4" />
            Edit Channel
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors font-medium flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div
        className={`rounded-xl p-4 ${
          channel.status === "active"
            ? "bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700"
            : channel.status === "pending"
            ? "bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700"
            : "bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700"
        }`}
      >
        <div className="flex items-center gap-3">
          {channel.status === "active" && (
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          )}
          {channel.status === "pending" && (
            <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          )}
          {channel.status === "inactive" && (
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          )}
          <div>
            <h3
              className={`font-semibold ${
                channel.status === "active"
                  ? "text-green-800 dark:text-green-300"
                  : channel.status === "pending"
                  ? "text-yellow-800 dark:text-yellow-300"
                  : "text-red-800 dark:text-red-300"
              }`}
            >
              {channel.status === "active" && "Channel Active"}
              {channel.status === "pending" && "Pending Setup"}
              {channel.status === "inactive" && "Channel Inactive"}
            </h3>
            <p
              className={`text-sm ${
                channel.status === "active"
                  ? "text-green-600 dark:text-green-400"
                  : channel.status === "pending"
                  ? "text-yellow-600 dark:text-yellow-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {channel.status === "active" &&
                "This channel is working properly and ready to receive messages."}
              {channel.status === "pending" &&
                "This channel needs configuration to start working."}
              {channel.status === "inactive" &&
                "This channel is not active and won't receive messages."}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "overview" && renderOverview()}
          {activeTab === "settings" && renderSettings()}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditChannelModal
          isOpen={showEditModal}
          channelData={channel}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveChannel}
          isSaving={isSaving}
        />
      )}

      {showDeleteModal && (
        <DeleteChannelModal
          isOpen={showDeleteModal}
          channelName={channel?.name}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteChannel}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default ChannelDetailPage;
